import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import IconButton from "../../DesignSystem/Buttons/IconButton";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import SummaryCards from "../../DesignSystem/SummaryCards/SummaryCards";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { getIncomePeriods } from "../utils/incomePeriods";
import { getProviderMatches } from "./providerMatchesService";
import { formatStatementDate, formatStatementDateOnly } from "../FCIStatements/statementDate";
import "../../../css/App.css";

const PAGE_SIZE = 250;
const DISPLAY_COLUMNS = [
  ["Index", "Index"],
  ["PaymentId", "Payment Entry"],
  ["MatchedAt", "Matched Date"],
  ["IOClientName", "IO Client"],
  ["IOBReference", "IOB"],
  ["IOPolicyNumber", "Policy Number"],
  ["IOAdviserName", "Adviser"],
  ["IOProvider", "Provider"],
  ["ProviderStatementId", "Statement ID"],
  ["ProviderStatementDate", "Statement Date"],
  ["ProviderStatementProvider", "Statement Provider"],
  ["ProviderStatementPolicyNumber", "Statement Policy Number"],
  ["ProviderStatementClientName", "Statement Client"],
  ["ProviderStatementAdviserName", "Statement Adviser"],
  ["PayableAmount", "Payable Amount"],
  ["GrossAmount", "Gross Amount"],
  // ["GeneratedSourceStatus", "Generated Source Status"],
  // ["GeneratedPolicyMatchStatus", "Generated Policy Match Status"],
  // ["GeneratedClientMatchStatus", "Generated Client Match Status"],
  // ["GeneratedProviderMatchStatus", "Generated Provider Match Status"],
];
const GENERATED_COLUMNS = new Set(DISPLAY_COLUMNS
  .map(([key]) => key)
  .filter((key) => key.startsWith("Generated")));
const MONEY_COLUMNS = new Set(["GrossAmount", "PayableAmount"]);

const formatPeriodName = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(text)
    ? formatStatementDateOnly(text)
    : text;
};
const money = (value) => Number(value || 0).toLocaleString("en-GB", {
  style: "currency",
  currency: "GBP",
});
const statusColour = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("mismatch") || text.includes("different") || text.includes("not found")) return "#fde8e8";
  if (text.includes("missing") || text.includes("suffix")) return "#fff4d6";
  return "#e5f5e8";
};

const ProviderMatchesPage = () => {
  const { userEmail } = useUserEmail();
  const [periodOptions, setPeriodOptions] = useState([]);
  const [period, setPeriod] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ rows: [], total: 0, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getIncomePeriods()
      .then((items) => {
        const options = [
          {
            id: "current",
            name: `Current - since ${items[0]?.endDatetime ? formatStatementDateOnly(items[0].endDatetime) : "latest close"}`,
          },
          ...items.map((item) => ({
            id: String(item.incomePeriodId),
            name: `${formatPeriodName(item.incomePeriodName || item.incomePeriodId)}${item.endDatetime ? ` - closes ${formatStatementDateOnly(item.endDatetime)}` : ""}`,
          })),
          { id: "all", name: "All income periods" },
        ];
        setPeriodOptions(options);
        setPeriod("current");
      })
      .catch((loadError) => setError(loadError.message));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    setError("");
    try {
      setResult(await getProviderMatches({
        page,
        pageSize: PAGE_SIZE,
        incomePeriodId: period,
        search: debouncedSearch,
        userEmail,
      }));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, period, userEmail]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(result.total / (result.pageSize || PAGE_SIZE)));
  const summaryCards = useMemo(() => [
    { id: "matches", label: "Matched payments", value: Number(result.total || 0).toLocaleString(), tone: "green" },
    { id: "period", label: "Income period", value: period === "current" ? "Current" : period === "all" ? "All" : period || "-", tone: "purple" },
    { id: "page", label: "Current page", value: `${page} of ${totalPages}`, tone: "teal" },
  ], [page, period, result.total, totalPages]);

  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const exported = await getProviderMatches({
        page: 1,
        pageSize: 100000,
        incomePeriodId: period,
        search,
        exportAll: true,
        userEmail,
      });
      const rows = (exported.rows || []).map((row) => {
        const output = {};
        Object.entries(row).forEach(([key, value]) => {
          if (key === "Index" || key.startsWith("Generated") || key === "ClientUrl" || key === "PlanUrl") return;
          const heading = key
            .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/\bId\b/g, "ID");
          output[heading] = key.includes("Date") || key === "MatchedAt"
            ? formatStatementDate(value)
            : value;
        });
        return output;
      });
      const headings = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
      const grossAmountIndex = headings.indexOf("Gross Amount");
      if (grossAmountIndex !== -1) {
        const [grossAmount] = headings.splice(grossAmountIndex, 1);
        headings.push(grossAmount);
      }
      const sheet = XLSX.utils.json_to_sheet(rows, { header: headings });
      const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
      sheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
      sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
      sheet["!cols"] = headings.map((heading) => ({ wch: Math.max(20, heading.length + 4) }));
      headings.forEach((heading, column) => {
        for (let row = 0; row <= range.e.r; row += 1) {
          const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (!cell) continue;
          cell.s = row === 0
            ? { fill: { fgColor: { rgb: "081E56" } }, font: { color: { rgb: "FFFFFF" }, bold: true } }
            : { fill: { fgColor: { rgb: row % 2 ? "EAF4FB" : "FFFFFF" } } };
        }
      });
      ["IOB Reference", "IO Policy Number"].forEach((heading) => {
        const column = headings.indexOf(heading);
        if (column < 0) return;
        (exported.rows || []).forEach((row, index) => {
          if (!row.PlanUrl) return;
          const cell = sheet[XLSX.utils.encode_cell({ r: index + 1, c: column })];
          if (!cell) return;
          cell.l = { Target: row.PlanUrl, Tooltip: "Open plan in Intelliflo" };
          cell.s = {
            ...cell.s,
            font: { color: { rgb: "0563C1" }, underline: true },
          };
        });
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Provider Matches");
      saveAs(
        new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })]),
        `provider-matches-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet><title>Provider Matches</title></Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container table-contained-page column gap-m">
        <Breadcrumb items={[
          { label: "Finance", to: "/Department/Finance" },
          { label: "Provider Matches" },
        ]} />
        <header className="row space-between gap-l">
          <div className="fit-column gap-xs">
            <h3 className="FP_H3">Provider Matches</h3>
            <p className="FP_Help_Text primary">
              Review every matched IO payment against its provider statement source.
            </p>
          </div>
          <div className="row gap-s justify-flex-end">
            <div className="fci-period-control">
              <CustomSelect
                label="Income period"
                options={periodOptions}
                value={period}
                overlay={loading}
                onChange={(event) => { setPage(1); setPeriod(event.target.value); }}
              />
            </div>
            <div className="fci-search-control">
              <SearchField
                id="provider-matches-search"
                value={search}
                label="Search"
                onChange={(event) => { setPage(1); setSearch(event.target.value); }}
                placeholder="Search all provider matches"
              />
            </div>
          </div>
        </header>
        <div className="row space-between gap-l">
          <IconButton
            icon={<DownloadIcon aria-hidden="true" />}
            label="Export Excel"
            loadingLabel="Exporting Excel..."
            loading={exporting}
            onClick={exportExcel}
            disabled={exporting || loading}
          />
          {totalPages > 1 ? (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          ) : null}
        </div>
        <section className="bg-white card-corner-radius p-m fit-column gap-s">
          <SummaryCards cards={summaryCards} grid="3" loading={loading} />
        </section>
        {error ? <p className="FP_Help_Text text-error" role="alert">{error}</p> : null}
        <section className="card-corner-radius">
          <div className="table-scroll-container">
            <table>
              <thead>
                <tr>
                  {DISPLAY_COLUMNS.map(([key, label]) => (
                    <th key={key} className="FP_Help_Text_Semibold uppercase white-text">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={DISPLAY_COLUMNS.length} className="FP_Help_Text primary">Loading provider matches...</td></tr>
                ) : result.rows?.length ? result.rows.map((row) => (
                  <tr key={row.PaymentId}>
                    {DISPLAY_COLUMNS.map(([key]) => {
                      const content = key === "MatchedAt" || key === "ProviderStatementDate"
                        ? formatStatementDate(row[key])
                        : MONEY_COLUMNS.has(key) ? money(row[key]) : String(row[key] ?? "");
                      return (
                        <td
                          key={key}
                          className={`FP_Help_Text text-black ${MONEY_COLUMNS.has(key) ? "text-align-right" : ""}`}
                          style={GENERATED_COLUMNS.has(key) ? { backgroundColor: statusColour(row[key]), fontWeight: 600 } : undefined}
                        >
                          {key === "IOClientName" && row.ClientUrl
                            ? <a className="table-url-link text-primary-1" href={row.ClientUrl} target="_blank" rel="noreferrer">{content}</a>
                            : (key === "IOBReference" || key === "IOPolicyNumber") && row.PlanUrl
                              ? <a className="table-url-link text-primary-1" href={row.PlanUrl} target="_blank" rel="noreferrer">{content}</a>
                              : content}
                        </td>
                      );
                    })}
                  </tr>
                )) : (
                  <tr><td colSpan={DISPLAY_COLUMNS.length} className="FP_Help_Text primary">No provider matches found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProviderMatchesPage;
